import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';

const ProductionQueuePanel = ({ queue, loading, onBatchPrint, onRefresh }) => {
  const [selectedCards, setSelectedCards] = useState([]);
  const [filterStatus, setFilterStatus] = useState('all');

  const filteredQueue = queue?.filter(item => 
    filterStatus === 'all' || item?.status === filterStatus
  ) || [];

  const handleSelectCard = (cardId) => {
    setSelectedCards(prev => 
      prev?.includes(cardId) 
        ? prev?.filter(id => id !== cardId)
        : [...prev, cardId]
    );
  };

  const handleSelectAll = () => {
    if (selectedCards?.length === filteredQueue?.length) {
      setSelectedCards([]);
    } else {
      setSelectedCards(filteredQueue?.map(item => item?.cardId));
    }
  };

  const handleBatchPrint = () => {
    if (selectedCards?.length === 0) {
      alert('Please select at least one card to print');
      return;
    }
    onBatchPrint(selectedCards);
    setSelectedCards([]);
  };

  if (loading) {
    return <div className="text-center py-12 text-gray-500">Loading print queue...</div>;
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-xl font-bold text-gray-900">Print Queue Management</h2>
          <p className="text-sm text-gray-600 mt-1">
            {filteredQueue?.length} jobs in queue • {selectedCards?.length} selected
          </p>
        </div>
        <div className="flex items-center gap-3">
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e?.target?.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          >
            <option value="all">All Status</option>
            <option value="pending">Pending</option>
            <option value="completed">Completed</option>
            <option value="failed">Failed</option>
          </select>
          <button
            onClick={handleBatchPrint}
            disabled={selectedCards?.length === 0}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:bg-gray-300 disabled:cursor-not-allowed"
          >
            <Icon name="Printer" className="w-4 h-4" />
            Print Selected ({selectedCards?.length})
          </button>
        </div>
      </div>
      {filteredQueue?.length === 0 ? (
        <div className="text-center py-12">
          <Icon name="Inbox" className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <p className="text-gray-500">No print jobs in queue</p>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left">
                  <input
                    type="checkbox"
                    checked={selectedCards?.length === filteredQueue?.length && filteredQueue?.length > 0}
                    onChange={handleSelectAll}
                    className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                  />
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Employee
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Department
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Printed At
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Printer
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Batch ID
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredQueue?.map((item) => (
                <tr key={item?.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4">
                    <input
                      type="checkbox"
                      checked={selectedCards?.includes(item?.cardId)}
                      onChange={() => handleSelectCard(item?.cardId)}
                      className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                    />
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900">{item?.employeeName}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-600">{item?.department}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium ${
                      item?.status === 'completed' ? 'bg-green-100 text-green-800' :
                      item?.status === 'failed'? 'bg-red-100 text-red-800' : 'bg-yellow-100 text-yellow-800'
                    }`}>
                      <Icon 
                        name={item?.status === 'completed' ? 'CheckCircle' : item?.status === 'failed' ? 'XCircle' : 'Clock'} 
                        className="w-3 h-3" 
                      />
                      {item?.status}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                    {new Date(item.printedAt)?.toLocaleString()}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                    {item?.printerModel || 'N/A'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                    {item?.batchId ? item?.batchId?.substring(0, 8) : 'Single'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default ProductionQueuePanel;